import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Form, Nav, Tab } from "react-bootstrap";
import {
  type Case,
  type ContentGetMiscContentFilteredRequest,
  // Definition import is intentional, for create
  type Definition,
  DocumentType,
  MiscContentType,
  PartyPosition,
  Representative,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import { FormModal } from "@/components/Modals";
import "./AddEditCaseModal.scss";
import { ArrowCounterclockwise } from "react-bootstrap-icons";
import { Typeahead, type TypeaheadRef } from "react-bootstrap-typeahead";
import type { MultiValue } from "react-select";
import PetitionerRespondentToggle from "@/components/CaseManagement/PetitionerRespondentToggle";
import {
  blankId,
  checkAndAddJurisdictionToOffice,
} from "@/components/ConfirmInfo/SourceDocumentReview";
import { SortVenue } from "@/components/DocumentWizard/SpecialInterrogatoriesStep/Selections/utils";
import FederalSelect from "@/components/FederalSelect";
import Loading from "@/components/Loading";
import type { TagOption } from "@/components/Select/TagsSelect";
import SelectCreate from "@/components/Select/TagsSelectCreate";
import {
  useContentApi,
  useDefinitionsApi,
  useFirmApi,
  useTagApi,
} from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import {
  CaliforniaId,
  FamilyLawCaseType,
  FederalId,
  PETITIONER_AND_RESPONDENT_TYPES,
} from "@/lib/constants";
import type {
  CaseResponse,
  ClientResponse,
  DefinitionResponse,
  FirmClient,
  JurisdictionResponse,
  UserResponse,
  VenueResponse,
} from "@/lib/types";
import JurisdictionSelect from "../JurisdictionSelect";
import Attorneys from "./Attorneys";
import CaseTypeSelect, { type CaseTypeOption } from "./CaseTypeSelect";

interface Props {
  title: string;
  confirm: string;
  isShow: boolean;
  _case?: Case;
  newCaseTitle?: string;
  client?: ClientResponse;
  availableTags: Map<number, string> | undefined;
  setAvailableTags: Dispatch<SetStateAction<Map<number, string> | undefined>>;
  users: UserResponse[] | null;
  jurisdictions: JurisdictionResponse[];
  onClose: () => void;
  onConfirm: (_case: Case) => void;
}

export default function AddEditCaseModal({
  title,
  confirm,
  isShow,
  _case,
  newCaseTitle,
  client,
  availableTags,
  setAvailableTags,
  users,
  jurisdictions,
  onClose,
  onConfirm,
}: Props) {
  const { firm, setFirm } = useAuth();

  const [shortTitle, setShortTitle] = useState(
    (newCaseTitle || _case?.shortTitle) ?? "",
  );
  const [fullTitle, setFullTitle] = useState(_case?.title ?? "");
  const [caseNumber, setCaseNumber] = useState(_case?.caseNumber ?? "");
  const [isPetitionerAndRespondent, setIsPetitionerAndRespondent] = useState(
    _case?.usePetitionerAndRespondent ?? false,
  );
  const [judge, setJudge] = useState(_case?.judge ?? "");
  const [plaintiff, setPlaintiff] = useState(_case?.plaintiffs ?? "");
  const [defendant, setDefendant] = useState(_case?.defendants ?? "");
  const [billingCode, setBillingCode] = useState<string | undefined>(
    _case?.billingCode ?? "",
  );
  const [venue, setVenue] = useState(
    _case?.venue && _case?.venue !== blankId ? _case?.venue : "",
  );
  const [jurisdictionId, setJurisdictionId] = useState(
    _case?.jurisdiction && _case?.jurisdiction !== blankId
      ? _case?.jurisdiction
      : (firm?.offices?.find((x) => x.jurisdictions?.length)?.jurisdictions?.[0]
          ?.jurisdictionId ?? undefined),
  );
  const [position, setPosition] = useState(
    _case?.clientPosition || PartyPosition.Unknown,
  );
  const [representative, setRepresentative] = useState(
    _case?.representativeType || Representative.Individual,
  );
  const [caseType, setCaseType] = useState(_case?.caseType ?? "");
  const [trialDate, setTrialDate] = useState(_case?.trialDate ?? "");
  const [filedDate, setFiledDate] = useState(_case?.filedDate ?? "");
  const [contactFirst, setContactFirst] = useState(
    _case?.contactFirstName ?? "",
  );
  const [contactLast, setContactLast] = useState(_case?.contactLastName ?? "");
  const [contactEmail, setContactEmail] = useState(_case?.contactEmail ?? "");
  const [attorneys, setAttorneys] = useState(_case?.attorneys ?? []);
  const [tags, setTags] = useState(_case?.tagIds || []);
  const [addingTags, setAddingTags] = useState(new Map<number, string>());
  const [showAlert, setShowAlert] = useState(true);
  const [showContactOverride, setShowContactOverride] = useState(
    !!_case?.contactFirstName ||
      !!_case?.contactLastName ||
      !!_case?.contactEmail,
  );
  const [saving, setSaving] = useState(false);
  const [definitions, setDefinitions] = useState<DefinitionResponse[]>();
  const [deletingDefinitions, setDeletingDefinitions] = useState<
    DefinitionResponse[]
  >([]);
  const [extraTagInfo, setExtraTagInfo] = useState<
    Map<number, number> | undefined
  >();
  const definitionsApi = useDefinitionsApi();
  const contentApi = useContentApi();
  const tagApi = useTagApi();
  const typeaheadRef = useRef<TypeaheadRef>(null);
  const firmApi = useFirmApi();

  const jurisdiction = useMemo(() => {
    if (!jurisdictions || !firm) {
      return undefined;
    }

    const isJsxBlank = _case?.jurisdiction === blankId;
    const firmJx = firm.offices?.find((x) => x.jurisdictions?.length)
      ?.jurisdictions?.[0]?.jurisdictionId;

    if (isJsxBlank) {
      return jurisdictions.find((j) => j.id === firmJx);
    }

    return (
      jurisdictions?.find((j) => j.id === jurisdictionId) ??
      jurisdictions?.find((j) => j.id === firmJx)
    );
  }, [jurisdictions, firm, _case?.jurisdiction, jurisdictionId]);

  const caseTypes = jurisdiction?.caseClasses
    ?.filter((c) => c.subTypes)
    .flatMap((c) => [c.subTypes])
    .flat();

  const contractOverrideOpen =
    showContactOverride || contactFirst || contactLast || contactEmail;

  async function doSave() {
    setSaving(true);
    const toSave = _case ? { ..._case } : ({} as CaseResponse);
    toSave.shortTitle = shortTitle;
    toSave.title = fullTitle;
    toSave.caseNumber = caseNumber;
    toSave.judge = judge;
    toSave.plaintiffs = plaintiff;
    toSave.defendants = defendant;
    toSave.jurisdiction = jurisdiction?.id;
    toSave.billingCode = billingCode;
    toSave.venue = venue;
    toSave.clientPosition = position;
    toSave.representativeType = representative;
    toSave.caseType = caseType === "" ? undefined : caseType;
    toSave.trialDate = trialDate;
    toSave.filedDate = filedDate;
    toSave.usePetitionerAndRespondent = isPetitionerAndRespondent ?? false;
    toSave.contactFirstName = showContactOverride ? contactFirst : undefined;
    toSave.contactLastName = showContactOverride ? contactLast : undefined;
    toSave.contactEmail = showContactOverride ? contactEmail : undefined;
    toSave.attorneys = attorneys;
    toSave.lastActivity = new Date(Date.now()); //not passed to the back end, used to update the local model to display recently updated

    const tagsUpdate = [...tags].filter((x) => x > 0);
    const tagsToAdd = Array.from(addingTags.entries());
    for (let i = 0; i < tagsToAdd.length; i++) {
      const t = tagsToAdd[i];
      const newTag = await tagApi.tagCreateTag({
        tagCreateRequest: { tagName: t[1] },
      });
      if (newTag) {
        tagsUpdate.push(newTag.id);
      }
      setAvailableTags((q) => {
        const update = q ? new Map([...q]) : new Map<number, string>();
        update.set(newTag.id, newTag.name);

        return update;
      });
    }
    setTags(tagsUpdate);
    setAddingTags(new Map<number, string>());
    toSave.tagIds = tagsUpdate;
    await doDeleteDefinitions();
    await onConfirm(toSave);
    await checkAndAddJurisdictionToOffice(
      jurisdictionId ?? "",
      firm ?? ({} as FirmClient),
      firmApi,
    );

    /* update firm object with new office jurisdiction */
    const newFirm = [...(firm?.offices ?? [])];
    newFirm.push({
      id: firm?.offices?.[0]?.id ?? "",
      jurisdictions: [
        ...(firm?.offices?.[0]?.jurisdictions ?? []),
        {
          jurisdictionId: jurisdictionId ?? "",
        },
      ],
    });
    setFirm({ ...firm, offices: newFirm } as FirmClient);

    setSaving(false);
  }

  function reset() {
    setShortTitle(_case?.shortTitle ?? "");
    setFullTitle(_case?.title ?? "");
    setCaseNumber(_case?.caseNumber ?? "");
    setJudge(_case?.judge ?? "");
    setPlaintiff(_case?.plaintiffs ?? "");
    setIsPetitionerAndRespondent(_case?.usePetitionerAndRespondent ?? false);
    setDefendant(_case?.defendants ?? "");
    setShowContactOverride(false);
    setJurisdictionId(undefined);
    setVenue(_case?.venue ?? "");
    setPosition(_case?.clientPosition ?? PartyPosition.Unknown);
    setRepresentative(_case?.representativeType ?? Representative.None);
    setCaseType(_case?.caseType ?? "");
    setFiledDate(_case?.filedDate ?? "");
    setTrialDate(_case?.trialDate ?? "");
    setContactFirst(_case?.contactFirstName ?? "");
    setContactLast(_case?.contactLastName ?? "");
    setContactEmail(_case?.contactEmail ?? "");
    setAttorneys(_case?.attorneys ?? []);
    setBillingCode(_case?.billingCode ?? undefined);
    setTags(_case?.tagIds ?? []);
    setDefinitions(undefined);
    setDeletingDefinitions([]);
    setAddingTags(new Map<number, string>());
  }

  useEffect(() => {
    const query: ContentGetMiscContentFilteredRequest = {
      type: MiscContentType.DiscoveryQuestion,
      documentType: DocumentType.DiscoveryRequest,
      caseType: caseType ?? undefined,
      skipTagFiltering: true,
    };
    const extras = new Map<number, number>();
    Promise.all([
      contentApi.contentGetMiscContentFiltered(query),
      contentApi.contentGetDefaultMiscContentFiltered(query),
    ]).then(([update, defaultQuestions]) => {
      // TODO: use map instead
      update
        .flatMap((x) => x.tagIds ?? [])
        .forEach((x) => {
          const found = extras.get(x);
          extras.set(x, found ? found + 1 : 1);
        });

      // TODO: use map instead
      defaultQuestions
        .flatMap((x) => x.tagIds ?? [])
        .forEach((x) => {
          const found = extras.get(x);
          extras.set(x, found ? found + 1 : 1);
        });
      setExtraTagInfo(extras);
    });
  }, [caseType, contentApi]);

  function unsetContactInfo(set: boolean) {
    setContactFirst("");
    setContactLast("");
    setContactEmail("");
    setShowContactOverride(set);
  }

  async function doDeleteDefinitions() {
    for (const d of deletingDefinitions) {
      d.isDeleted = true;
      await definitionsApi.definitionUpdate({ definition: d });
    }
    setDeletingDefinitions([]);
  }

  useEffect(() => {
    if (newCaseTitle) {
      setShortTitle(newCaseTitle);
    }
  }, [newCaseTitle]);

  useEffect(() => {
    const getDefinitions = async () => {
      if (_case?.id) {
        setDefinitions(
          (
            (await definitionsApi.definitionGetCaseList({
              caseId: _case?.id,
            })) as DefinitionResponse[]
          ).filter((x) => !x.isDeleted && x.isInternal),
        );
      }
    };
    if (!definitions) {
      getDefinitions();
    }
  }, [_case?.id, definitionsApi, definitions]);

  const options: CaseTypeOption[] = useMemo(() => {
    if (!caseTypes || !jurisdiction?.caseClasses) {
      return [];
    }
    const vs = caseTypes?.flatMap((v) => [
      {
        value: v?.id || "",
        label: v?.shortName ?? "",
        key:
          jurisdiction.caseClasses?.find((x) => x.id === v?.caseClassId)
            ?.name ?? "",
      },
    ]);
    return vs;
  }, [caseTypes, jurisdiction?.caseClasses]);

  function handleCaseTypeChange(caseType: string) {
    setCaseType(caseType);
    // If the case type is family law and the petitioner and respondent are not set, set them to true
    if (caseType === FamilyLawCaseType && !isPetitionerAndRespondent) {
      setIsPetitionerAndRespondent(true);
    }
  }

  function handleTagChange(value: MultiValue<TagOption>) {
    setTags(
      value
        ?.map((x) => x.value)
        .filter((x): x is number => typeof x === "number") ?? [],
    );
    setAddingTags((q) => {
      const update = new Map(
        [...q].filter((a) => [...value.values()].find((v) => v.value === a[0])),
      );

      return update;
    });
  }

  // need to do this jazz because UserService enums are being returned as strings not numbers like the DocumentService,
  // probably need to change UserService to behave like DocumentService but there are too many cases to handle right now
  const positionValues = position.split(",").map((item) => item.trim());
  function flipPosition(val: PartyPosition) {
    const existing = positionValues.indexOf(val);

    if (existing !== -1) {
      positionValues.splice(existing, 1);
    } else {
      positionValues.push(val);
    }

    let positionVal = positionValues.join();
    if (positionVal.length && positionVal[0] === ",") {
      positionVal = positionVal.substring(1);
    }
    setPosition((positionVal || "Unknown") as PartyPosition);
  }

  function handleDefinitionChange(
    value: string,
    index: number,
    field: keyof Pick<DefinitionResponse, "term" | "aliases" | "content">,
  ) {
    setDefinitions((ds) => {
      const update = [...(ds ?? [])];
      const item = update[index];
      item[field] = value || null;
      return update;
    });
  }

  // TODO: retries/error handling
  async function handleDefinitionBlur(index: number) {
    const definition = definitions?.[index];
    if (!definition?.content) return;
    // New or edited definitions are always for the Case
    // Update
    if (definition.id && definition.caseId === _case?.id) {
      await definitionsApi.definitionUpdate({ definition });
    } else {
      // Use `Definition` type for create
      const def = definition as Definition;
      def.caseId = _case?.id || null;
      def.id = undefined;
      def.isInternal = true;
      const created = (await definitionsApi.definitionCreate({
        definition: def,
      })) as DefinitionResponse;
      if (created.id) {
        setDefinitions((ds) => {
          const update = [...(ds ?? [])];
          update[index] = created;
          return update;
        });
      }
    }
  }

  function handleDefinitionRemove(index: number) {
    let remove: DefinitionResponse[] = [];
    setDefinitions((ds) => {
      const update = [...(ds ?? [])];

      remove = update.splice(index, 1);

      return update;
    });

    setDeletingDefinitions((d) => [...d].concat(remove.filter((x) => x.id)));
  }

  function handleDefinitionRestore(index: number) {
    let restore: DefinitionResponse[] = [];

    setDeletingDefinitions((ds) => {
      const update = [...ds];

      restore = update.splice(index, 1);

      return update;
    });

    setDefinitions((d) => [...(d ?? [])].concat(restore));
  }

  //always allow federal
  const officeJurisdictions =
    firm?.offices?.flatMap((o) => {
      return o.jurisdictions?.map((j) => j.jurisdictionId ?? "") ?? "";
    }) ?? [];
  if (!officeJurisdictions.find((of) => of === FederalId)) {
    officeJurisdictions.push(FederalId);
  }

  // If the jurisdiction attached to the case does not exist on the list, add it on
  if (
    !officeJurisdictions.find((j) => j === _case?.jurisdiction) &&
    _case?.jurisdiction
  ) {
    officeJurisdictions.push(_case.jurisdiction);
  }

  /* biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset the form when the modal is shown and there is no case */
  useEffect(() => {
    if (isShow && !_case) {
      reset();
      setShortTitle("");
    }
  }, [isShow, _case]);

  return (
    <FormModal
      title={title}
      cancelText="Cancel"
      confirmText={confirm}
      show={isShow}
      disableConfirmButton={
        !shortTitle ||
        !position ||
        !caseType ||
        !venue ||
        !jurisdictionId ||
        saving
      }
      onClose={onClose}
      onConfirm={doSave}
      size="lg"
      contentClassName="add-edit-case"
      preventBackgroundClick
      confirming={saving}
    >
      {!_case && showAlert && (
        <Alert variant="info" dismissible onClose={() => setShowAlert(false)}>
          <b>Did you know?</b> You don't need to create a Case to upload
          documents, you can add the Case during Document Confirmation.
        </Alert>
      )}
      <Tab.Container id="case-modal-tabs" defaultActiveKey="general">
        <Nav variant="underline" className="case-modal-nav mb-3">
          <Nav.Item>
            <Nav.Link eventKey="general">General Details</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="admin">Admin Details</Nav.Link>
          </Nav.Item>
          {
            //don't show definitions or parties when creating new cases
            !!_case?.id && (
              <Nav.Item>
                <Nav.Link eventKey="definitions">Definitions</Nav.Link>
              </Nav.Item>
            )
          }
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="general">
            <div className="full">
              <div className="client-input-row">
                <label htmlFor="short-title" className="required">
                  Short Title of Case
                </label>
                <Form.Control
                  data-testid="short-title"
                  id="short-title"
                  required
                  value={shortTitle || undefined}
                  onChange={(e) => setShortTitle(e.target.value)}
                />
              </div>
              <div className="client-input-row">
                <div className="input-right-col">
                  <label htmlFor="case-number">Case Number</label>
                  <Form.Control
                    id="case-number"
                    value={caseNumber || undefined}
                    onChange={(e) => setCaseNumber(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="full">
              <div className="client-input-row full">
                <label htmlFor="full-title">Full Title</label>
                <Form.Control
                  id="full-title"
                  value={fullTitle || undefined}
                  onChange={(e) => setFullTitle(e.target.value)}
                />
              </div>
              <div className="full">
                <div className="client-input-row">
                  <Form.Group>
                    <div className="section-header-row">
                      <label htmlFor="client-position">Client Position</label>
                    </div>
                    <PetitionerRespondentToggle
                      value={isPetitionerAndRespondent ?? false}
                      onChange={setIsPetitionerAndRespondent}
                      idPrefix="add-edit-case-petitioner-respondent"
                    />
                    <div className="checkbox-container">
                      <span>
                        <Form.Check
                          data-testid="client-position-plaintiff"
                          id={"propounding-party-plaintiff"}
                          type={"checkbox"}
                          label={
                            isPetitionerAndRespondent
                              ? PETITIONER_AND_RESPONDENT_TYPES.PETITIONER
                              : PETITIONER_AND_RESPONDENT_TYPES.PLAINTIFF
                          }
                          checked={positionValues.includes(
                            PartyPosition.Plaintiff,
                          )}
                          onChange={() => flipPosition(PartyPosition.Plaintiff)}
                          isInvalid={position === PartyPosition.Unknown}
                        />
                      </span>
                      <span>
                        <Form.Check
                          id={"propounding-party-cross-defendant"}
                          type={"checkbox"}
                          label={
                            isPetitionerAndRespondent
                              ? PETITIONER_AND_RESPONDENT_TYPES.CROSS_RESPONDENT
                              : PETITIONER_AND_RESPONDENT_TYPES.CROSS_DEFENDANT
                          }
                          checked={positionValues.includes(
                            PartyPosition.CrossDefendant,
                          )}
                          onChange={() =>
                            flipPosition(PartyPosition.CrossDefendant)
                          }
                          isInvalid={position === PartyPosition.Unknown}
                        />
                      </span>
                      <span>
                        <Form.Check
                          id={"propounding-party-defendant"}
                          type={"checkbox"}
                          label={
                            isPetitionerAndRespondent
                              ? PETITIONER_AND_RESPONDENT_TYPES.RESPONDENT
                              : PETITIONER_AND_RESPONDENT_TYPES.DEFENDANT
                          }
                          checked={positionValues.includes(
                            PartyPosition.Defendant,
                          )}
                          onChange={() => flipPosition(PartyPosition.Defendant)}
                          isInvalid={position === PartyPosition.Unknown}
                        />
                      </span>
                      <span>
                        <Form.Check
                          id={"propounding-party-cross-complainant"}
                          type={"checkbox"}
                          label={
                            isPetitionerAndRespondent
                              ? PETITIONER_AND_RESPONDENT_TYPES.CROSS_PETITIONER
                              : PETITIONER_AND_RESPONDENT_TYPES.CROSS_COMPLAINANT
                          }
                          checked={positionValues.includes(
                            PartyPosition.CrossComplainant,
                          )}
                          onChange={() =>
                            flipPosition(PartyPosition.CrossComplainant)
                          }
                          isInvalid={position === PartyPosition.Unknown}
                        />
                      </span>
                    </div>
                  </Form.Group>
                </div>
                <div className="client-input-row">
                  <Form.Group>
                    <label htmlFor="representative-action">
                      Representative Action
                    </label>
                    <div className="checkbox-container">
                      <Form.Check
                        id={"representative-ind"}
                        type={"radio"}
                        label="Individual"
                        checked={
                          !representative ||
                          representative === Representative.Individual
                        }
                        value={Representative.Individual}
                        onChange={(e) =>
                          setRepresentative(
                            e.currentTarget.value as Representative,
                          )
                        }
                        className="mb-1"
                      />
                      <Form.Check
                        id={"representative-class"}
                        type={"radio"}
                        label="Class Action"
                        checked={representative === Representative.ClassAction}
                        value={Representative.ClassAction}
                        onChange={(e) =>
                          setRepresentative(
                            e.currentTarget.value as Representative,
                          )
                        }
                        className="mb-1"
                      />
                      <Form.Check
                        id={"representative-paga"}
                        type={"radio"}
                        label="PAGA"
                        checked={representative === Representative.Paga}
                        value={Representative.Paga}
                        onChange={(e) =>
                          setRepresentative(
                            e.currentTarget.value as Representative,
                          )
                        }
                        className="mb-1"
                      />
                    </div>
                  </Form.Group>
                </div>
              </div>
            </div>
            <div className="full">
              <div className="client-input-row">
                <label htmlFor="case-type" className="required">
                  Case Type
                </label>
                <CaseTypeSelect
                  options={options}
                  onChange={handleCaseTypeChange}
                  selected={caseType}
                />
              </div>
              <div className="client-input-row">
                <div className="input-right-col">
                  <label htmlFor="judge">Judge</label>
                  <Form.Control
                    id="judge"
                    value={judge || undefined}
                    onChange={(e) => setJudge(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="full">
              <div className="client-input-row">
                <label htmlFor="jurisdiction" className="required">
                  Jurisdiction
                </label>
                {(officeJurisdictions?.length ?? 0) <= 1 ? (
                  <Form.Control
                    id="jurisdiction"
                    value={jurisdiction?.searchNames?.[0] ?? ""}
                    disabled
                  />
                ) : (
                  <JurisdictionSelect
                    setJurisdiction={(value: string) => {
                      setJurisdictionId(value);
                      // if the jurisdiction is changed, reset the venue
                      if (value !== jurisdictionId) {
                        setVenue("");
                      }
                    }}
                    jurisdiction={jurisdictionId ?? ""}
                    jurisdictions={jurisdictions}
                    yourJurisdictions={jurisdictions
                      ?.filter((j) => {
                        return officeJurisdictions?.includes(j.id ?? "");
                      })
                      .sort(
                        (a, b) =>
                          a.friendlyName?.localeCompare(b.friendlyName ?? "") ??
                          0,
                      )}
                    isDisabled={false}
                  />
                )}
              </div>
              <div data-testid="venue" className="client-input-row">
                <div className="input-right-col">
                  <label htmlFor="venue" className="required">
                    Venue
                  </label>
                  {jurisdiction?.id === CaliforniaId ? (
                    <Form.Select
                      id="venue"
                      required
                      value={venue}
                      onChange={(e) => setVenue(e.target.value)}
                    >
                      <option value="" disabled>
                        Please Select
                      </option>
                      {jurisdiction?.venues
                        ?.sort(SortVenue)
                        .map((venue, index) => (
                          <option key={index} value={venue.id}>
                            {venue.shortName}
                          </option>
                        ))}
                    </Form.Select>
                  ) : jurisdictionId === FederalId ? (
                    <FederalSelect
                      setVenue={(e: string) => setVenue(e)}
                      venue={venue}
                      federalJurisdiction={jurisdiction}
                      jurisdictions={jurisdictions}
                      officeJxIds={firm?.offices?.flatMap(
                        (of) =>
                          of.jurisdictions?.flatMap(
                            (jx) => jx.jurisdictionId ?? "",
                          ) ?? "",
                      )}
                    />
                  ) : (
                    <Typeahead
                      onChange={(selected) =>
                        setVenue((selected[0] as VenueResponse)?.id ?? "")
                      }
                      selected={
                        (venue &&
                          [
                            jurisdiction?.venues?.find((x) => x.id === venue),
                          ].filter(
                            (x): x is NonNullable<typeof x> => x !== undefined,
                          )) ||
                        []
                      }
                      isInvalid={!venue}
                      ref={typeaheadRef}
                      id="venue-select"
                      options={jurisdiction?.venues?.sort(SortVenue) || []}
                      placeholder="Select a Venue..."
                      labelKey={(option) =>
                        `${(option as VenueResponse).shortName}`
                      }
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="full">
              <div className="client-input-row">
                <label htmlFor="plaintiff">
                  {isPetitionerAndRespondent
                    ? PETITIONER_AND_RESPONDENT_TYPES.PETITIONER
                    : PETITIONER_AND_RESPONDENT_TYPES.PLAINTIFF}
                </label>
                <Form.Control
                  id="plaintiff"
                  value={plaintiff || undefined}
                  onChange={(e) => setPlaintiff(e.target.value)}
                />
              </div>
              <div className="client-input-row">
                <div className="input-right-col">
                  <label htmlFor="defendant">
                    {isPetitionerAndRespondent
                      ? PETITIONER_AND_RESPONDENT_TYPES.RESPONDENT
                      : PETITIONER_AND_RESPONDENT_TYPES.DEFENDANT}
                  </label>
                  <Form.Control
                    id="defendant"
                    value={defendant || undefined}
                    onChange={(e) => setDefendant(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="client-input-row full">
              <SelectCreate
                id="case-tags"
                label="Tags"
                availableTags={availableTags}
                addingTags={addingTags}
                setAddingTags={setAddingTags}
                extraInfo={extraTagInfo}
                selected={tags}
                onChange={handleTagChange}
              />
            </div>
          </Tab.Pane>
          <Tab.Pane eventKey="admin">
            <div className="client-input-row">
              <label htmlFor="filed-date">Filed Date</label>
              <Form.Control
                type="date"
                className="case-management-date-picker"
                id="filed-date"
                value={filedDate || undefined}
                onChange={(e) => setFiledDate(e.target.value)}
              />
            </div>
            <div className="client-input-row">
              <div className="input-right-col">
                <label htmlFor="trial-date">Trial Date</label>
                <Form.Control
                  type="date"
                  className="case-management-date-picker"
                  id="trial-date"
                  value={trialDate || undefined}
                  onChange={(e) => setTrialDate(e.target.value)}
                />
              </div>
            </div>
            <div className="client-input-row">
              <label htmlFor="client-billing">Billing Code</label>
              <Form.Control
                id="client-billing"
                value={billingCode}
                onChange={(e) => setBillingCode(e.target.value)}
              />
            </div>
            <h4>Case Contact Information</h4>
            <Form.Check
              type={"checkbox"}
              label="Use Client Contact info"
              checked={!contractOverrideOpen}
              onChange={(e) => unsetContactInfo(!e.target.checked)}
            />
            {contractOverrideOpen && (
              <>
                <div className="client-input-row">
                  <label htmlFor="contact-first">First Name</label>
                  <Form.Control
                    id="contact-first"
                    value={contactFirst || undefined}
                    onChange={(e) => setContactFirst(e.target.value)}
                  />
                </div>
                <div className="client-input-row">
                  <label htmlFor="contact-last">Last Name</label>
                  <Form.Control
                    id="contact-last"
                    value={contactLast || undefined}
                    onChange={(e) => setContactLast(e.target.value)}
                  />
                </div>
                <div className="client-input-row">
                  <label htmlFor="contact-email">Email Address</label>
                  <Form.Control
                    id="contact-email"
                    value={contactEmail || undefined}
                    onChange={(e) => setContactEmail(e.target.value)}
                  />
                </div>
              </>
            )}
            <Attorneys
              users={users}
              assignedAttorneys={attorneys}
              topLevelAttorneys={client?.attorneys ?? []}
              setAttorneys={setAttorneys}
            />
          </Tab.Pane>
          {
            //don't show definitions or parties when creating new cases
            !!_case?.id && (
              <Tab.Pane eventKey="definitions">
                <Loading isLoading={!_case && !definitions}>
                  <div className="definitions-container">
                    {definitions
                      ?.sort((a, b) => {
                        if (!a.id && b.id) {
                          return 1;
                        }

                        if (a.id && !b.id) {
                          return -1;
                        }

                        if (!a.term && b.term) {
                          return 1;
                        }

                        if (a.term && !b.term) {
                          return -1;
                        }

                        if (!a.term && !b.term) {
                          return 0;
                        }

                        return a.term?.localeCompare(b.term ?? "") ?? 0;
                      })
                      .map((def, index) => {
                        if (def.isDeleted) return null;
                        return (
                          <div key={`definition-${index}`}>
                            <label htmlFor={`definition-term-${index}`}>
                              Term
                            </label>
                            <Form.Control
                              id={`definition-term-${index}`}
                              value={def.term ?? ""}
                              onChange={(e) =>
                                handleDefinitionChange(
                                  e.target.value,
                                  index,
                                  "term",
                                )
                              }
                              onBlur={() => handleDefinitionBlur(index)}
                            />
                            <label htmlFor={`definition-alias-${index}`}>
                              Aliases (Optional)
                            </label>
                            <Form.Control
                              id={`definition-alias-${index}`}
                              value={def.aliases ?? ""}
                              placeholder="Separate multiple aliases with a comma"
                              onChange={(e) =>
                                handleDefinitionChange(
                                  e.target.value,
                                  index,
                                  "aliases",
                                )
                              }
                              onBlur={() => handleDefinitionBlur(index)}
                            />
                            <label htmlFor={`definition-content-${index}`}>
                              Content
                            </label>
                            <Form.Control
                              as="textarea"
                              id={`definition-content-${index}`}
                              value={def.content ?? ""}
                              onChange={(e) =>
                                handleDefinitionChange(
                                  e.target.value,
                                  index,
                                  "content",
                                )
                              }
                              onBlur={() => handleDefinitionBlur(index)}
                            />
                            <Button
                              variant="link"
                              onClick={() => handleDefinitionRemove(index)}
                            >
                              Remove
                            </Button>
                          </div>
                        );
                      })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setDefinitions((d) => [
                        ...(d ?? []),
                        {
                          isInternal: true,
                          id: 0,
                          userId: null,
                          caseId: null,
                          documentId: null,
                        },
                      ])
                    }
                  >
                    + Add Another Term
                  </Button>
                  {!!deletingDefinitions.length && <h2>To Delete on Save:</h2>}
                  {deletingDefinitions.map((def, index) => {
                    return (
                      <div key={`definition-delete-${index}`}>
                        {`${def.term} `}
                        <ArrowCounterclockwise
                          title="Undo"
                          onClick={() => handleDefinitionRestore(index)}
                        />
                      </div>
                    );
                  })}
                </Loading>
              </Tab.Pane>
            )
          }
        </Tab.Content>
      </Tab.Container>
    </FormModal>
  );
}
